import { Vec3, sub, dot, scale, length, lengthSq } from '../math/vec3'
import { CelestialBody } from '../world/body'
import { densityAt } from '../world/atmosphere'
import { RigidState } from '../dynamics/rigidBody'
import { atmosphereVelocity, altitudeOf } from '../dynamics/flight'
import { localUp } from './attitude'
import { elementsFromState, apoapsisRadius, periapsisRadius, orbitalPeriod, isClosed } from '../orbit/elements'
import { trueToMean, meanMotion } from '../orbit/kepler'

const TAU = Math.PI * 2

/** Velocita rispetto al suolo, che ruota col pianeta. */
export const surfaceVelocity = (state: RigidState, body: CelestialBody): Vec3 =>
  sub(state.velocity, atmosphereVelocity(state.position, body))

export const verticalSpeed = (state: RigidState, body: CelestialBody): number =>
  dot(surfaceVelocity(state, body), localUp(state.position))

export function horizontalSpeed(state: RigidState, body: CelestialBody): number {
  const v = surfaceVelocity(state, body)
  const up = localUp(state.position)
  return length(sub(v, scale(up, dot(v, up))))
}

/** Pressione dinamica q = ½ρv², il numero che dice quando il razzo si spezza. */
export function dynamicPressure(state: RigidState, body: CelestialBody): number {
  const rho = densityAt(body.atmosphere, altitudeOf(state.position, body))
  if (rho <= 0) return 0
  return 0.5 * rho * lengthSq(surfaceVelocity(state, body))
}

export interface OrbitReadouts {
  /** QUOTE sopra la superficie, non raggi */
  readonly apoapsis: number
  readonly periapsis: number
  readonly eccentricity: number
  readonly period: number
  readonly timeToApoapsis: number
  readonly timeToPeriapsis: number
}

export function orbitReadouts(state: RigidState, body: CelestialBody): OrbitReadouts {
  const el = elementsFromState(state.position, state.velocity, body.mu, 0)
  const chiusa = isClosed(el)

  // tempo agli apsidi dall'anomalia media, che cresce uniformemente nel tempo
  const M = trueToMean(el.trueAnomaly, el.ecc)
  const n = meanMotion(el)
  const daAnomalia = (target: number): number => {
    if (!chiusa) return Infinity
    return ((((target - M) % TAU) + TAU) % TAU) / n
  }

  return {
    apoapsis: chiusa ? apoapsisRadius(el) - body.radius : Infinity,
    periapsis: periapsisRadius(el) - body.radius,
    eccentricity: el.ecc,
    period: orbitalPeriod(el),
    timeToApoapsis: daAnomalia(Math.PI),
    timeToPeriapsis: daAnomalia(0),
  }
}
